import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import Shepherd from 'shepherd.js';
import { TranslateService } from '@ngx-translate/core';
import { checkIsWebView } from '../../../helpers/screen-utils';

@Injectable({ providedIn: 'root' })
export class PostsTourService {
  private _pendingTour: string | null = null;

  constructor(private router: Router, private translate: TranslateService) {}

  private t(key: string): string {
    return this.translate.instant(key);
  }

  setPendingTour(tourId: string): void {
    this._pendingTour = tourId;
  }

  consumePendingTour(): string | null {
    const t = this._pendingTour;
    this._pendingTour = null;
    return t;
  }

  private cancelActiveTour(): void {
    if (Shepherd.activeTour) {
      Shepherd.activeTour.cancel();
    }
  }

  private get tourDefaults() {
    return {
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: true,
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 4,
      },
    };
  }

  /**
   * Phase 1: starts on the Posts feed page.
   * On desktop, highlights the "New Post" button.
   * On mobile, highlights the "+" FAB and navigates to the editor on Next.
   * Navigates to /apps/posts first if not already there.
   */
  startFeedTour(): void {
    this.cancelActiveTour();
    const isMobile = !checkIsWebView();

    const doStart = () => {
      if (isMobile) {
        this.waitForElement('#tour-post-fab').then(() => this.showMobileFabStep());
      } else {
        this.showDesktopNewPostStep();
      }
    };

    if (this.router.url.startsWith('/apps/posts')) {
      doStart();
    } else {
      this.router.navigate(['/apps/posts']).then(() => setTimeout(doStart, 300));
    }
  }

  private showMobileFabStep(): void {
    const tour = new Shepherd.Tour(this.tourDefaults);

    // If the user taps the real FAB instead of the tour's "Next" button,
    // detect the navigation and start phase 2.
    const navSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && event.urlAfterRedirects.startsWith('/apps/posts/edit/')) {
        navSub.unsubscribe();
        this.setPendingTour('basic-post');
        tour.cancel();
      }
    });

    tour.addStep({
      id: 'new-post-fab',
      title: this.t('guidedTour.posts.fab.title'),
      text: this.t('guidedTour.posts.fab.text'),
      attachTo: { element: '#tour-post-fab', on: 'top' },
      buttons: [
        {
          text: this.t('guidedTour.btn.endTour'),
          action: () => { navSub.unsubscribe(); tour.cancel(); },
          secondary: true,
        },
        {
          text: this.t('guidedTour.btn.next'),
          action: () => {
            navSub.unsubscribe();
            tour.cancel();
            this.setPendingTour('basic-post');
            this.router.navigate(['/apps/posts/edit/0']);
          },
        },
      ],
    });
    tour.start();
  }

  private showDesktopNewPostStep(): void {
    const tour = new Shepherd.Tour(this.tourDefaults);

    // If the user clicks the real "New Post" button (routerLink) instead of the
    // tour's "Open Post Editor" button, detect the navigation and start phase 2.
    const navSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && event.urlAfterRedirects.startsWith('/apps/posts/edit/')) {
        navSub.unsubscribe();
        this.setPendingTour('basic-post');
        tour.cancel();
      }
    });

    tour.addStep({
      id: 'new-post-btn',
      title: this.t('guidedTour.posts.newPostBtn.title'),
      text: this.t('guidedTour.posts.newPostBtn.text'),
      attachTo: { element: '#tour-new-post-btn', on: 'bottom' },
      buttons: [
        {
          text: this.t('guidedTour.btn.endTour'),
          action: () => { navSub.unsubscribe(); tour.cancel(); },
          secondary: true,
        },
        {
          text: this.t('guidedTour.btn.openPostEditor'),
          action: () => {
            navSub.unsubscribe();
            this.setPendingTour('basic-post');
            tour.cancel();
            this.router.navigate(['/apps/posts/edit/0']);
          },
        },
      ],
    });
    tour.start();
  }

  private waitForElement(selector: string, timeoutMs = 0): Promise<void> {
    return new Promise(resolve => {
      const deadline = timeoutMs > 0 ? Date.now() + timeoutMs : 0;
      const check = () => {
        const el = document.querySelector(selector);
        if (el && el.getBoundingClientRect().width > 0) {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        } else if (deadline > 0 && Date.now() >= deadline) {
          resolve(); // timed out — let when.show handle the absent element
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }


  /**
   * Guardian / read-only tour: runs on the Posts feed page.
   * Walks Guardians through the feed, the Feed/Highlights tabs (mobile),
   * filtering, and the Upcoming Events and Forms & Attachments sidebar (desktop).
   */
  startGuardianFeedTour(): void {
    this.cancelActiveTour();
    const isMobile = !checkIsWebView();

    const doStart = () => {
      this.waitForElement('#tour-posts-feed').then(() => {
        const tour = new Shepherd.Tour(this.tourDefaults);

        const next     = () => tour.next();
        const back     = () => tour.back();
        const cancel   = () => tour.cancel();
        const backBtn   = { text: this.t('guidedTour.btn.back'),    action: back,   secondary: true };
        const cancelBtn = { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true };
        const nextBtn   = { text: this.t('guidedTour.btn.next'),    action: next };
        const finishBtn = { text: this.t('guidedTour.btn.finish'),  action: () => tour.complete() };

        // Step 1: Post feed overview
        tour.addStep({
          id: 'guardian-feed',
          title: this.t('guidedTour.posts.guardianFeed.title'),
          text:  this.t('guidedTour.posts.guardianFeed.text'),
          attachTo: { element: '#tour-posts-feed', on: isMobile ? 'bottom' : 'right' },
          buttons: [cancelBtn, nextBtn],
        });

        if (isMobile) {
          // Step 2 (mobile): Feed / Highlights tabs
          tour.addStep({
            id: 'guardian-tabs',
            title: this.t('guidedTour.posts.guardianTabs.title'),
            text:  this.t('guidedTour.posts.guardianTabs.text'),
            attachTo: { element: '#tour-posts-tabs', on: 'bottom' },
            buttons: [backBtn, cancelBtn, nextBtn],
          });

          // Step 3 (mobile): Filter button
          tour.addStep({
            id: 'guardian-filter',
            title: this.t('guidedTour.posts.guardianFilter.title'),
            text:  this.t('guidedTour.posts.guardianFilter.text'),
            attachTo: { element: '#tour-posts-filter-btn', on: 'bottom' },
            buttons: [backBtn, cancelBtn, finishBtn],
          });
        } else {
          // Step 2 (desktop): Filter button — skip gracefully if panel is already open
          tour.addStep({
            id: 'guardian-filter',
            title: this.t('guidedTour.posts.guardianFilter.title'),
            text:  this.t('guidedTour.posts.guardianFilter.text'),
            attachTo: { element: '#tour-posts-filter-btn', on: 'bottom' },
            buttons: [backBtn, cancelBtn, nextBtn],
            when: {
              show: () => {
                if (!document.querySelector('#tour-posts-filter-btn')) {
                  setTimeout(() => tour.next(), 0);
                }
              },
            },
          });

          // Step 3 (desktop): Upcoming Events sidebar — skip if sidebar is collapsed
          tour.addStep({
            id: 'guardian-events',
            title: this.t('guidedTour.posts.guardianEvents.title'),
            text:  this.t('guidedTour.posts.guardianEvents.text'),
            attachTo: { element: '#tour-posts-events', on: 'left' },
            buttons: [backBtn, cancelBtn, nextBtn],
            when: {
              show: () => {
                if (!document.querySelector('#tour-posts-events')) {
                  setTimeout(() => tour.next(), 0);
                }
              },
            },
          });

          // Step 4 (desktop): Forms & Attachments sidebar — skip if sidebar is collapsed
          tour.addStep({
            id: 'guardian-addons',
            title: this.t('guidedTour.posts.guardianAddons.title'),
            text:  this.t('guidedTour.posts.guardianAddons.text'),
            attachTo: { element: '#tour-posts-addons', on: 'left' },
            buttons: [backBtn, cancelBtn, finishBtn],
            when: {
              show: () => {
                if (!document.querySelector('#tour-posts-addons')) {
                  setTimeout(() => tour.complete(), 0);
                }
              },
            },
          });
        }

        tour.start();
      });
    };

    if (this.router.url.startsWith('/apps/posts')) {
      doStart();
    } else {
      this.router.navigate(['/apps/posts']).then(() => setTimeout(doStart, 300));
    }
  }

  /**
   * Phase 2: runs inside posts-edit for a standard (simple) post.
   * Each step targets a section of the form by its tour anchor ID.
   * Optional sections (Post Tag, Post On Behalf Of, etc.) attach gracefully
   * as floating tooltips if the element is not present for a given user.
   */
  startEditTour(options: { showSchoolSelector?: boolean } = {}): void {
    this.cancelActiveTour();
    const isMobile = !checkIsWebView();
    const tour = new Shepherd.Tour(this.tourDefaults);

    const next = () => tour.next();
    const back = () => tour.back();
    const cancel = () => tour.cancel();

    const backBtn   = { text: this.t('guidedTour.btn.back'),    action: back,   secondary: true };
    const cancelBtn = { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true };
    const nextBtn   = { text: this.t('guidedTour.btn.next'),    action: next };
    const finishBtn = { text: this.t('guidedTour.btn.finish'),  action: () => tour.complete() };

    // Step 1: School selector — only shown for multi-school users; first step so no Back
    if (options.showSchoolSelector) {
      tour.addStep({
        id: 'school-selector',
        title: this.t('guidedTour.posts.schoolSelector.title'),
        text: this.t('guidedTour.posts.schoolSelector.text'),
        attachTo: { element: '#tour-school-selector', on: 'bottom' },
        beforeShowPromise: () => this.waitForElement('#tour-school-selector', 2000),
        buttons: [cancelBtn, nextBtn],
      });
    }

    // Step 2: Select All Staff / All Parents shortcuts (only visible when groups data has loaded)
    // Uses beforeShowPromise to wait up to 1s for the element (async group data may not be ready yet),
    // then skips via when.show if it still hasn't appeared (user doesn't have this feature).
    tour.addStep({
      id: 'select-all-groups',
      title: this.t('guidedTour.posts.selectAllGroups.title'),
      text: this.t('guidedTour.posts.selectAllGroups.text'),
      attachTo: { element: '#tour-select-all-groups', on: 'bottom' },
      buttons: options.showSchoolSelector ? [backBtn, cancelBtn, nextBtn] : [cancelBtn, nextBtn],
      beforeShowPromise: () => new Promise<void>(resolve => {
        let waited = 0;
        const check = () => {
          if (document.querySelector('#tour-select-all-groups') || waited >= 1000) {
            resolve();
          } else {
            waited += 50;
            setTimeout(check, 50);
          }
        };
        check();
      }),
      when: {
        show: () => {
          if (!document.querySelector('#tour-select-all-groups')) {
            if (!options.showSchoolSelector) {
              // recipient-groups is the first visible step on this page — remove Back.
              tour.getById('recipient-groups')?.updateStepOptions({ buttons: [cancelBtn, nextBtn] });
            } else {
              // school-selector was shown but select-all-groups is being skipped.
              // Default back() would loop back through the skipped step, so jump
              // directly to school-selector instead.
              const backToSchool = { text: this.t('guidedTour.btn.back'), action: () => tour.show('school-selector'), secondary: true };
              tour.getById('recipient-groups')?.updateStepOptions({ buttons: [cancelBtn, backToSchool, nextBtn] });
            }
            setTimeout(() => tour.next(), 0);
          }
        },
      },
    });

    // Step 3: Recipient groups dropdown (first step when no school selector)
    tour.addStep({
      id: 'recipient-groups',
      title: this.t('guidedTour.posts.recipientGroups.title'),
      text: this.t('guidedTour.posts.recipientGroups.text'),
      attachTo: { element: '#tour-recipient-groups', on: 'bottom' },
      buttons: [backBtn, cancelBtn, nextBtn],
    });

    // Step 3: One-time group
    // On mobile, attach to the section header (above the button) with 'bottom' so the
    // tooltip sits between the header and the button, keeping the button visible.
    tour.addStep({
      id: 'create-one-time-group',
      title: this.t('guidedTour.posts.oneTimeGroup.title'),
      text: this.t('guidedTour.posts.oneTimeGroup.text'),
      attachTo: { element: isMobile ? '#tour-one-time-group-header' : '#tour-create-one-time-group', on: isMobile ? 'bottom' : 'top' },
      buttons: [backBtn, cancelBtn, nextBtn],
    });

    // Step 4: Subject
    tour.addStep({
      id: 'subject',
      title: this.t('guidedTour.posts.subject.title'),
      text: this.t('guidedTour.posts.subject.text'),
      attachTo: { element: '#tour-subject-fields', on: 'bottom' },
      buttons: [backBtn, cancelBtn, nextBtn],
    });

    // Step 5: Description
    tour.addStep({
      id: 'description',
      title: this.t('guidedTour.posts.description.title'),
      text: this.t('guidedTour.posts.description.text'),
      attachTo: { element: '#tour-description-section', on: 'bottom' },
      buttons: [backBtn, cancelBtn, nextBtn],
    });

    // Step 6: Save as template
    tour.addStep({
      id: 'save-as-template',
      title: this.t('guidedTour.posts.saveAsTemplate.title'),
      text: this.t('guidedTour.posts.saveAsTemplate.text'),
      attachTo: { element: '#tour-save-as-template', on: 'top' },
      buttons: [backBtn, cancelBtn, nextBtn],
    });

    if (isMobile) {
      // ── Mobile steps 7–11 ──────────────────────────────────────────────────
      // Rather than trying to show steps inside bottom-sheet dialogs, we guide
      // the user through the bottom bar buttons directly.

      // Bar overview: explains that more buttons appear once fields are filled.
      // Also sets the mobile-options step's buttons while it is still hidden —
      // updating a step that isn't rendered yet avoids the Shepherd/Popper.js
      // re-render that hides the popup when updateStepOptions is called on a
      // currently-visible step.
      tour.addStep({
        id: 'mobile-options-bar',
        title: this.t('guidedTour.posts.mobileOptionsBar.title'),
        text: this.t('guidedTour.posts.mobileOptionsBar.text'),
        attachTo: { element: '#tour-mobile-options-bar', on: 'top' },
        buttons: [backBtn, cancelBtn, nextBtn],
        when: {
          show: () => {
            const hasMoreBarButtons = !!(
              document.querySelector('#tour-mobile-addons-btn') ||
              document.querySelector('#tour-mobile-dates-btn')
            );
            tour.getById('mobile-options')?.updateStepOptions({
              buttons: hasMoreBarButtons ? [backBtn, cancelBtn, nextBtn] : [backBtn, cancelBtn, finishBtn],
            });
          },
        },
      });

      // Options button — always visible; user can tap to explore and close the sheet.
      // Buttons are set by the previous step's when.show (see above).
      tour.addStep({
        id: 'mobile-options',
        title: this.t('guidedTour.posts.mobileOptions.title'),
        text: this.t('guidedTour.posts.mobileOptions.text'),
        attachTo: { element: '#tour-mobile-options-btn', on: 'top' },
        buttons: [backBtn, cancelBtn, nextBtn],
      });

      // Addons button — only rendered when canSavePost; skip gracefully if absent.
      tour.addStep({
        id: 'mobile-addons',
        title: this.t('guidedTour.posts.addons.title'),
        text: this.t('guidedTour.posts.addons.text'),
        attachTo: { element: '#tour-mobile-addons-btn', on: 'top' },
        buttons: [backBtn, cancelBtn, nextBtn],
        when: {
          show: () => {
            if (!document.querySelector('#tour-mobile-addons-btn')) {
              setTimeout(() => tour.next(), 0);
            }
          },
        },
      });

      // Dates button — only rendered when canSavePost; skip gracefully if absent.
      tour.addStep({
        id: 'mobile-dates',
        title: this.t('guidedTour.posts.mobileDates.title'),
        text: this.t('guidedTour.posts.mobileDates.text'),
        attachTo: { element: '#tour-mobile-dates-btn', on: 'top' },
        buttons: [backBtn, cancelBtn, nextBtn],
        when: {
          show: () => {
            if (!document.querySelector('#tour-mobile-dates-btn')) {
              setTimeout(() => tour.next(), 0);
            }
          },
        },
      });

      // Publish section — only rendered when canSavePost; complete tour if absent.
      tour.addStep({
        id: 'mobile-publish',
        title: this.t('guidedTour.posts.mobilePublish.title'),
        text: this.t('guidedTour.posts.mobilePublish.text'),
        attachTo: { element: '#tour-publish-section', on: 'top' },
        buttons: [cancelBtn, backBtn, finishBtn],
        when: {
          show: () => {
            if (!document.querySelector('#tour-publish-section')) {
              setTimeout(() => tour.complete(), 0);
            }
          },
        },
      });

    } else {
      // ── Desktop steps 7–15 ────────────────────────────────────────────────

      // Step 7: Add-ons — only visible after required fields are filled.
      // Defer the DOM check to beforeShowPromise so it reflects live DOM state,
      // not the state at tour-build time (when the fields are still empty).
      tour.addStep({
        id: 'addons',
        title: this.t('guidedTour.posts.addons.title'),
        text: this.t('guidedTour.posts.addons.text'),
        attachTo: { element: '#tour-addons-section', on: 'top' },
        buttons: [backBtn, cancelBtn, nextBtn],
        beforeShowPromise: () => {
          const step = tour.getById('addons')!;
          if (document.querySelector('#tour-addons-section')) {
            step.updateStepOptions({
              title: this.t('guidedTour.posts.addons.title'),
              text: this.t('guidedTour.posts.addons.text'),
              attachTo: { element: '#tour-addons-section', on: 'top' },
            });
          } else {
            step.updateStepOptions({
              title: this.t('guidedTour.posts.addonsHidden.title'),
              text: this.t('guidedTour.posts.addonsHidden.text'),
              attachTo: undefined,
            });
          }
          return Promise.resolve();
        },
      });

      // Step 8: Mark as time sensitive
      tour.addStep({
        id: 'time-sensitive',
        title: this.t('guidedTour.posts.timeSensitive.title'),
        text: this.t('guidedTour.posts.timeSensitive.text'),
        attachTo: { element: '#tour-time-sensitive', on: 'left' },
        buttons: [backBtn, cancelBtn, nextBtn],
      });

      // Step 9: Receive a copy
      tour.addStep({
        id: 'receive-copy',
        title: this.t('guidedTour.posts.receiveCopy.title'),
        text: this.t('guidedTour.posts.receiveCopy.text'),
        attachTo: { element: '#tour-receive-copy', on: 'left' },
        buttons: [backBtn, cancelBtn, nextBtn],
      });

      // Step 11: Post Tag
      tour.addStep({
        id: 'post-tag',
        title: this.t('guidedTour.posts.postTag.title'),
        text: this.t('guidedTour.posts.postTag.text'),
        attachTo: { element: '#tour-post-tag', on: 'left' },
        buttons: [backBtn, cancelBtn, nextBtn],
      });

      // Step 12: Post On Behalf Of (conditional — not available to all users)
      tour.addStep({
        id: 'post-behalf',
        title: this.t('guidedTour.posts.postBehalf.title'),
        text: this.t('guidedTour.posts.postBehalf.text'),
        attachTo: { element: '#tour-post-behalf', on: 'left' },
        buttons: [backBtn, cancelBtn, nextBtn],
        when: {
          show: () => {
            if (!document.querySelector('#tour-post-behalf')) {
              tour.next();
            }
          },
        },
      });

      // Step 13: Responders
      tour.addStep({
        id: 'post-responders',
        title: this.t('guidedTour.posts.postResponders.title'),
        text: this.t('guidedTour.posts.postResponders.text'),
        attachTo: { element: '#tour-post-responders', on: 'left' },
        buttons: [backBtn, cancelBtn, nextBtn],
      });

      // Step 14: Scheduling dates
      tour.addStep({
        id: 'dates',
        title: this.t('guidedTour.posts.dates.title'),
        text: this.t('guidedTour.posts.dates.text'),
        attachTo: { element: '#tour-dates-section', on: 'left' },
        buttons: [backBtn, cancelBtn, nextBtn],
      });

      // Step 15: Publish Options — only visible after required fields are filled.
      // Defer the DOM check to beforeShowPromise for the same reason as the addons step.
      tour.addStep({
        id: 'publish',
        title: this.t('guidedTour.posts.publish.title'),
        text: this.t('guidedTour.posts.publish.text'),
        attachTo: { element: '#tour-publish-section', on: 'top' },
        buttons: [cancelBtn, backBtn, finishBtn],
        beforeShowPromise: () => {
          const step = tour.getById('publish')!;
          if (document.querySelector('#tour-publish-section')) {
            step.updateStepOptions({
              title: this.t('guidedTour.posts.publish.title'),
              text: this.t('guidedTour.posts.publish.text'),
              attachTo: { element: '#tour-publish-section', on: 'top' },
            });
          } else {
            step.updateStepOptions({
              title: this.t('guidedTour.posts.publishHidden.title'),
              text: this.t('guidedTour.posts.publishHidden.text'),
              attachTo: undefined,
            });
          }
          return Promise.resolve();
        },
      });
    }

    tour.start();
  }
}
